// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays, startOfMonth } from "date-fns";

import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

import {
	getRevenueSummaryData,
	getRevenueByDateChartData,
	getAnalyticsSummaryData,
	getTopCountriesRevenueChartData,
	getTopDomainsRevenueChartData,
	getSolutionRevenueChartData,
	getAnalyticsByDateChartData,
	getSolutionAnalyticsChartData,
	getTopDomainsAnalyticsChartData,
	getTopCountriesAnalyticsChartData
} from "../utils/dashboardDataService";

import { getDomainsForPartner } from "../utils/partnerMap";
import { useAuth } from "../auth/AuthContext";

import {
	fetchAnalyticsDomains,
	fetchRevenueDomains,
	fetchAnalyticsCountries,
	fetchRevenueCountries
} from "../utils/dashboardApiService";

import { downloadRevenueAndAnalyticsCSVs } from "../utils/dashboardDownloadUtils";

// ✅ Option-1 cache
import { useDashboardCache } from "../utils/DashboardCacheContext";

/* ---------------------------- ECHARTS HELPERS ---------------------------- */

const makeBarGradient = (top, bottom) =>
	new echarts.graphic.LinearGradient(0, 0, 0, 1, [
		{ offset: 0, color: top },
		{ offset: 1, color: bottom }
	]);

const baseGrid = {
	left: 56,
	right: 56,
	top: 54,
	bottom: 64,
	containLabel: true
};

const baseAxis = {
	axisLine: { lineStyle: { color: "#cbd5e1" } },
	axisTick: { show: false },
	axisLabel: { color: "#334155", fontSize: 11 }
};

const baseSplitLine = { lineStyle: { color: "#e2e8f0" } };
const baseTooltip = { trigger: "axis" };

const niceAxisRange = (
	values = [],
	{ minZero = true, splitNumber = 5, pad = 0.12 } = {}
) => {
	const nums = (values || []).map(Number).filter((n) => Number.isFinite(n));
	const maxVal = nums.length ? Math.max(...nums) : 0;
	const minVal = nums.length ? Math.min(...nums) : 0;

	let min = minZero ? 0 : minVal;
	let max = maxVal;

	if (!Number.isFinite(max) || max <= 0) max = 1;
	max = max * (1 + pad);

	const pow = Math.pow(10, Math.floor(Math.log10(max)));
	const n = max / pow;
	const niceN = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
	const niceMax = niceN * pow;

	const interval = niceMax / splitNumber;
	const alignedMax = interval * splitNumber;

	if (!minZero) min = Math.floor(min / interval) * interval;

	return { min, max: alignedMax, interval, splitNumber };
};

/**
 * ✅ 3D Custom bars (ECharts "custom" series)
 * FIX: Tiny values produced < 1px height bars and were returned as null.
 * We now enforce a minimum pixel height via minBarPx.
 */
const make3DBarSeries = ({
	name,
	values = [],
	topColor,
	bottomColor,
	sideColor = "rgba(29,78,216,0.50)",
	topFaceColor = "rgba(147,197,253,0.80)",
	depth = 8,
	widthRatio = 0.22,
	minBarPx = 3 // ✅ minimum visible bar height in pixels
}) => {
	return {
		name,
		type: "custom",
		encode: { x: 0, y: 1 },
		data: (values || []).map((v, i) => [i, Number(v || 0)]),
		tooltip: { trigger: "item" },
		renderItem: (params, api) => {
			const idx = api.value(0);
			const val = Number(api.value(1) || 0);

			const coordTop = api.coord([idx, val]);
			const coordBot = api.coord([idx, 0]);

			const band = api.size([1, 0])[0];

			const targetPx = 34;
			const barW = Math.min(targetPx, band * widthRatio);

			const x = coordTop[0];
			let yTop = coordTop[1];
			const yBot = coordBot[1];

			if (!Number.isFinite(yTop) || !Number.isFinite(yBot)) return null;
			if (val <= 0) return null;

			const heightPx = yBot - yTop;
			if (heightPx > 0 && heightPx < minBarPx) {
				yTop = yBot - minBarPx;
			}
			if (yBot - yTop < 1) return null;

			const dx = depth;
			const dy = depth * 0.55;

			const A = [x - barW, yTop];
			const B = [x + barW, yTop];
			const C = [x + barW, yBot];
			const D = [x - barW, yBot];

			const A2 = [A[0] + dx, A[1] - dy];
			const B2 = [B[0] + dx, B[1] - dy];
			const C2 = [C[0] + dx, C[1] - dy];

			const front = {
				type: "polygon",
				shape: { points: [A, B, C, D] },
				style: {
					fill: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{ offset: 0, color: topColor },
						{ offset: 1, color: bottomColor }
					]),
					shadowBlur: 14,
					shadowColor: "rgba(0,0,0,0.14)",
					shadowOffsetY: 6
				}
			};

			const top = {
				type: "polygon",
				shape: { points: [A, B, B2, A2] },
				style: { fill: topFaceColor }
			};

			const side = {
				type: "polygon",
				shape: { points: [B, C, C2, B2] },
				style: { fill: sideColor }
			};

			return { type: "group", children: [side, top, front] };
		}
	};
};

const makeBarLineOption = ({
	data,
	xKey,
	barKey,
	lineKey,
	barName,
	lineName,
	leftAxisLabel,
	rightAxisLabel,
	leftFormatter,
	rightFormatter,
	barGradientTop,
	barGradientBottom,
	lineColor,
	xLabelRotate = 0,
	is3D = true,
	barWidthRatio = 0.22,
	splitNumber = 5,
	minBarPx = 3
}) => {
	const xData = (data || []).map((d) => d?.[xKey] ?? "");
	const barData = (data || []).map((d) => Number(d?.[barKey] ?? 0));
	const lineData = (data || []).map((d) => Number(d?.[lineKey] ?? 0));

	let xData2 = [...xData];
	let barData2 = [...barData];
	let lineData2 = [...lineData];

	if (xData2.length === 1) {
		xData2 = ["", xData2[0], ""];
		barData2 = [0, barData2[0], 0];
		lineData2 = [null, lineData2[0], null];
	}

	const leftRange = niceAxisRange(barData, { minZero: true, splitNumber });
	const rightRange = niceAxisRange(lineData, { minZero: true, splitNumber });

	return {
		grid: baseGrid,
		tooltip: baseTooltip,
		legend: {
			top: 8,
			left: "center",
			itemWidth: 18,
			itemHeight: 10,
			textStyle: { color: "#334155", fontSize: 11 }
		},
		xAxis: {
			type: "category",
			data: xData2,
			...baseAxis,
			boundaryGap: true,
			axisTick: { alignWithLabel: true },
			axisLabel: {
				...baseAxis.axisLabel,
				rotate: xLabelRotate,
				margin: 14,
				overflow: "truncate",
				width: 90,
				formatter: (v) => String(v || "").slice(0, 22)
			}
		},
		yAxis: [
			{
				type: "value",
				name: leftAxisLabel,
				min: leftRange.min,
				max: leftRange.max,
				interval: leftRange.interval,
				splitNumber: leftRange.splitNumber,
				...baseAxis,
				splitLine: baseSplitLine,
				nameGap: 18,
				axisLabel: {
					...baseAxis.axisLabel,
					formatter: leftFormatter || ((v) => v)
				}
			},
			{
				type: "value",
				name: rightAxisLabel,
				min: rightRange.min,
				max: rightRange.max,
				interval: rightRange.interval,
				splitNumber: rightRange.splitNumber,
				...baseAxis,
				splitLine: { show: false },
				nameGap: 18,
				axisLabel: {
					...baseAxis.axisLabel,
					formatter: rightFormatter || ((v) => v)
				}
			}
		],
		series: [
			...(is3D
				? [
						make3DBarSeries({
							name: barName,
							values: barData2,
							topColor: barGradientTop,
							bottomColor: barGradientBottom,
							sideColor: "rgba(2, 132, 199, 0.30)",
							topFaceColor: "rgba(147, 197, 253, 0.80)",
							depth: 8,
							widthRatio: barWidthRatio,
							minBarPx
						})
				  ]
				: [
						{
							name: barName,
							type: "bar",
							data: barData2,
							barWidth: 18,
							barMinHeight: minBarPx,
							itemStyle: {
								borderRadius: [8, 8, 0, 0],
								color: makeBarGradient(barGradientTop, barGradientBottom),
								shadowBlur: 14,
								shadowColor: "rgba(0,0,0,0.18)",
								shadowOffsetY: 6
							}
						}
				  ]),
			{
				name: lineName,
				type: "line",
				yAxisIndex: 1,
				data: lineData2,
				connectNulls: false,
				smooth: true,
				symbol: "circle",
				symbolSize: 7,
				lineStyle: { width: 3, color: lineColor },
				itemStyle: { color: lineColor }
			}
		]
	};
};

const Chart = ({ option }) => (
	<ReactECharts
		option={option}
		style={{ height: 300, width: "100%" }}
		notMerge={true}
		lazyUpdate={true}
	/>
);

/* ---------------------------- UI HELPERS ---------------------------- */

const SkeletonCard = ({ className = "" }) => (
	<div
		className={`rounded-2xl bg-white shadow-sm border border-sky-100 p-4 animate-pulse ${className}`}
	>
		<div className="h-4 w-40 rounded bg-slate-200" />
		<div className="mt-3 h-[260px] w-full rounded bg-slate-100" />
	</div>
);

const SkeletonStat = () => (
	<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center animate-pulse">
		<div className="h-3 w-20 rounded bg-slate-200" />
		<div className="mt-2 h-7 w-16 rounded bg-slate-100" />
	</div>
);

const InlineOverlayLoading = ({ text = "Loading..." }) => (
	<div className="absolute inset-0 rounded-2xl bg-white/70 backdrop-blur-[1px] flex items-center justify-center">
		<div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm">
			{text}
		</div>
	</div>
);

/* ---------------------------- DASHBOARD PAGE ---------------------------- */

const Dashboard = () => {
	const auth = useAuth?.() || {};
	const { get, set } = useDashboardCache();

	// ✅ IMPORTANT: use dataPartnerName for ALL data calls (admin alias aware)
	const partnerName =
		auth.dataPartnerName || auth.partnerName || auth.user?.partnerName || null;

	// auth token
	const token = auth.token || auth.accessToken || auth.user?.token || null;

	const [analyticsDomainsRows, setAnalyticsDomainsRows] = useState([]);
	const [revenueDomainsRows, setRevenueDomainsRows] = useState([]);
	const [analyticsCountriesRows, setAnalyticsCountriesRows] = useState([]);
	const [revenueCountriesRows, setRevenueCountriesRows] = useState([]);

	const [loadingAll, setLoadingAll] = useState(false);
	const [loadingCountries, setLoadingCountries] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	// ✅ blocks UI until first successful load for the current date range
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

	const [dateRange, setDateRange] = useState({ start: null, end: null });
	const [selectedDomain, setSelectedDomain] = useState("all");

	const lastDateKeyRef = useRef(null);
	const lastDomainKeyRef = useRef(null);

	// ✅ Reset domain and local “already fetched” refs when partner changes
	useEffect(() => {
		setSelectedDomain("all");
		lastDateKeyRef.current = null;
		lastDomainKeyRef.current = null;
		setHasLoadedOnce(false);

		// also clear local rows so UI doesn't show stale partner
		setAnalyticsDomainsRows([]);
		setRevenueDomainsRows([]);
		setAnalyticsCountriesRows([]);
		setRevenueCountriesRows([]);
	}, [partnerName]);

	const domainOptions = useMemo(() => {
		const list = getDomainsForPartner(partnerName);
		const uniq = Array.from(new Set(list || []));
		if (!uniq.length) return ["all"];
		if (String(uniq[0]).toLowerCase() !== "all") return ["all", ...uniq];
		return uniq;
	}, [partnerName]);

	useEffect(() => {
		if (!domainOptions?.length) return;
		if (!domainOptions.includes(selectedDomain)) setSelectedDomain("all");
	}, [domainOptions, selectedDomain]);

	const filters = useMemo(() => {
		const startStr = dateRange.start
			? format(dateRange.start, "yyyy-MM-dd")
			: null;
		const endStr = dateRange.end ? format(dateRange.end, "yyyy-MM-dd") : null;
		return { startDate: startStr, endDate: endStr, domain: selectedDomain };
	}, [dateRange.start, dateRange.end, selectedDomain]);

	const dateKey = useMemo(() => {
		if (!filters.startDate || !filters.endDate) return "";
		return `${filters.startDate}__${filters.endDate}`;
	}, [filters.startDate, filters.endDate]);

	const domainKey = useMemo(() => selectedDomain, [selectedDomain]);

	const canFetch = Boolean(
		partnerName && token && filters.startDate && filters.endDate
	);
	const isInitialBlockingLoading = canFetch && !hasLoadedOnce;

	/* ---------------------------- STABLE SAFE ARRAYS ---------------------------- */

	const EMPTY_ARR = useMemo(() => [], []);

	const safeAnalyticsDomainsRows = useMemo(
		() => (hasLoadedOnce ? analyticsDomainsRows : EMPTY_ARR),
		[hasLoadedOnce, analyticsDomainsRows, EMPTY_ARR]
	);
	const safeRevenueDomainsRows = useMemo(
		() => (hasLoadedOnce ? revenueDomainsRows : EMPTY_ARR),
		[hasLoadedOnce, revenueDomainsRows, EMPTY_ARR]
	);
	const safeAnalyticsCountriesRows = useMemo(
		() => (hasLoadedOnce ? analyticsCountriesRows : EMPTY_ARR),
		[hasLoadedOnce, analyticsCountriesRows, EMPTY_ARR]
	);
	const safeRevenueCountriesRows = useMemo(
		() => (hasLoadedOnce ? revenueCountriesRows : EMPTY_ARR),
		[hasLoadedOnce, revenueCountriesRows, EMPTY_ARR]
	);

	/* ---------------------------- CACHE KEYS ---------------------------- */

	// Cache only “domains rows” by partner+date
	const domainsCacheKey = useMemo(() => {
		if (!partnerName || !dateKey) return "";
		return `dash:v1:domains:${partnerName}:${dateKey}`;
	}, [partnerName, dateKey]);

	// Cache only “countries rows” by partner+date+domain
	const countriesCacheKey = useMemo(() => {
		if (!partnerName || !dateKey) return "";
		return `dash:v1:countries:${partnerName}:${dateKey}:${domainKey}`;
	}, [partnerName, dateKey, domainKey]);

	/* ---------------------------- FETCHING ---------------------------- */

	// 1) Date change (or partner change): hydrate/fetch domains rows, and hydrate/fetch countries for current domain
	useEffect(() => {
		if (!partnerName || !token || !filters.startDate || !filters.endDate)
			return;

		const controller = new AbortController();
		const currentDateRefKey = `${partnerName}__${dateKey}`;
		const currentDomainRefKey = `${partnerName}__${domainKey}__${dateKey}`;

		(async () => {
			// Avoid double-run within same mount
			if (lastDateKeyRef.current === currentDateRefKey) return;

			// Block UI until domains rows are ready
			setHasLoadedOnce(false);
			setLoadingAll(true);
			setErrorMsg("");

			try {
				// --------- DOMAINS (domains+revenue by domain) ----------
				const cachedDomains = domainsCacheKey ? get(domainsCacheKey) : null;
				let aDomains = cachedDomains?.data?.analyticsDomainsRows;
				let rDomains = cachedDomains?.data?.revenueDomainsRows;

				if (!aDomains || !rDomains) {
					[aDomains, rDomains] = await Promise.all([
						fetchAnalyticsDomains({
							partnerName,
							startDate: filters.startDate,
							endDate: filters.endDate,
							token,
							signal: controller.signal
						}),
						fetchRevenueDomains({
							partnerName,
							startDate: filters.startDate,
							endDate: filters.endDate,
							token,
							signal: controller.signal
						})
					]);

					if (domainsCacheKey) {
						set(domainsCacheKey, {
							analyticsDomainsRows: aDomains,
							revenueDomainsRows: rDomains
						});
					}
				}

				setAnalyticsDomainsRows(aDomains || []);
				setRevenueDomainsRows(rDomains || []);

				// Domains rows ready → allow main UI
				setHasLoadedOnce(true);

				// --------- COUNTRIES (for current domain) ----------
				// not blocking skeleton; show overlay if needed
				const cachedCountries = countriesCacheKey
					? get(countriesCacheKey)
					: null;

				let aCountries = cachedCountries?.data?.analyticsCountriesRows;
				let rCountries = cachedCountries?.data?.revenueCountriesRows;

				if (aCountries && rCountries) {
					setAnalyticsCountriesRows(aCountries || []);
					setRevenueCountriesRows(rCountries || []);
				} else {
					setLoadingCountries(true);
					const [aC, rC] = await Promise.all([
						fetchAnalyticsCountries({
							partnerName,
							startDate: filters.startDate,
							endDate: filters.endDate,
							domain: domainKey,
							token,
							signal: controller.signal
						}),
						fetchRevenueCountries({
							partnerName,
							startDate: filters.startDate,
							endDate: filters.endDate,
							domain: domainKey,
							token,
							signal: controller.signal
						})
					]);

					setAnalyticsCountriesRows(aC || []);
					setRevenueCountriesRows(rC || []);

					if (countriesCacheKey) {
						set(countriesCacheKey, {
							analyticsCountriesRows: aC,
							revenueCountriesRows: rC
						});
					}
				}

				// update refs so domain effect won't immediately refetch
				lastDateKeyRef.current = currentDateRefKey;
				lastDomainKeyRef.current = currentDomainRefKey;
			} catch (err) {
				if (err?.name === "AbortError") return;
				setErrorMsg(err?.message || "Failed to load dashboard data");
			} finally {
				setLoadingAll(false);
				setLoadingCountries(false);
			}
		})();

		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [partnerName, token, dateKey, domainKey]);

	// 2) Domain change: fetch/hydrate only countries
	useEffect(() => {
		if (!partnerName || !token || !filters.startDate || !filters.endDate)
			return;

		const controller = new AbortController();
		const currentDomainRefKey = `${partnerName}__${domainKey}__${dateKey}`;

		(async () => {
			// must have a date load first
			if (!lastDateKeyRef.current) return;

			// same domain+date already handled
			if (lastDomainKeyRef.current === currentDomainRefKey) return;

			// hydrate from cache first
			const cachedCountries = countriesCacheKey ? get(countriesCacheKey) : null;
			if (cachedCountries?.data) {
				setAnalyticsCountriesRows(
					cachedCountries.data.analyticsCountriesRows || []
				);
				setRevenueCountriesRows(
					cachedCountries.data.revenueCountriesRows || []
				);
				lastDomainKeyRef.current = currentDomainRefKey;
				return;
			}

			lastDomainKeyRef.current = currentDomainRefKey;
			setLoadingCountries(true);
			setErrorMsg("");

			try {
				const [aCountries, rCountries] = await Promise.all([
					fetchAnalyticsCountries({
						partnerName,
						startDate: filters.startDate,
						endDate: filters.endDate,
						domain: domainKey,
						token,
						signal: controller.signal
					}),
					fetchRevenueCountries({
						partnerName,
						startDate: filters.startDate,
						endDate: filters.endDate,
						domain: domainKey,
						token,
						signal: controller.signal
					})
				]);

				setAnalyticsCountriesRows(aCountries || []);
				setRevenueCountriesRows(rCountries || []);

				if (countriesCacheKey) {
					set(countriesCacheKey, {
						analyticsCountriesRows: aCountries,
						revenueCountriesRows: rCountries
					});
				}
			} catch (err) {
				if (err?.name === "AbortError") return;
				setErrorMsg(err?.message || "Failed to load country data");
			} finally {
				setLoadingCountries(false);
			}
		})();

		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [partnerName, token, domainKey, dateKey]);

	/* ---------------- COMPUTED DATA ---------------- */

	const revenueSummaryData = useMemo(
		() =>
			getRevenueSummaryData(
				safeAnalyticsDomainsRows,
				safeRevenueDomainsRows,
				filters
			),
		[safeAnalyticsDomainsRows, safeRevenueDomainsRows, filters]
	);

	const revenueByDateData = useMemo(
		() =>
			getRevenueByDateChartData(
				safeAnalyticsDomainsRows,
				safeRevenueDomainsRows,
				filters
			),
		[safeAnalyticsDomainsRows, safeRevenueDomainsRows, filters]
	);

	const solutionRevenueChartData = useMemo(
		() =>
			getSolutionRevenueChartData(
				safeAnalyticsDomainsRows,
				safeRevenueDomainsRows,
				filters
			),
		[safeAnalyticsDomainsRows, safeRevenueDomainsRows, filters]
	);

	const topDomainsRevenueData = useMemo(
		() =>
			getTopDomainsRevenueChartData(
				safeAnalyticsDomainsRows,
				safeRevenueDomainsRows,
				filters
			),
		[safeAnalyticsDomainsRows, safeRevenueDomainsRows, filters]
	);

	const topCountriesRevenueData = useMemo(
		() =>
			getTopCountriesRevenueChartData(
				safeAnalyticsCountriesRows,
				safeRevenueCountriesRows
			),
		[safeAnalyticsCountriesRows, safeRevenueCountriesRows]
	);

	const analyticsSummaryData = useMemo(
		() => getAnalyticsSummaryData(safeAnalyticsDomainsRows, filters),
		[safeAnalyticsDomainsRows, filters]
	);

	const analyticsByDateData = useMemo(
		() => getAnalyticsByDateChartData(safeAnalyticsDomainsRows, filters),
		[safeAnalyticsDomainsRows, filters]
	);

	const solutionAnalyticsData = useMemo(
		() => getSolutionAnalyticsChartData(safeAnalyticsDomainsRows, filters),
		[safeAnalyticsDomainsRows, filters]
	);

	const topDomainsAnalyticsData = useMemo(
		() => getTopDomainsAnalyticsChartData(safeAnalyticsDomainsRows, filters),
		[safeAnalyticsDomainsRows, filters]
	);

	const topCountriesAnalyticsData = useMemo(
		() =>
			getTopCountriesAnalyticsChartData(safeAnalyticsCountriesRows, filters),
		[safeAnalyticsCountriesRows, filters]
	);

	const handleDateChangeFromHeader = (range) => setDateRange(range);

	/* ---------------- DOWNLOAD (2 CSVs) ---------------- */

	const handleDownload = () => {
		if (!hasLoadedOnce) return;

		downloadRevenueAndAnalyticsCSVs({
			partnerName,
			filters,
			revenueDomainRows: safeRevenueDomainsRows,
			analyticsDomainRows: safeAnalyticsDomainsRows
		});
	};

	/* ---------------- OPTIONS ---------------- */

	const rpmFmt = (v) => Number(v || 0).toFixed(2);

	const optRevenueByDate = useMemo(
		() =>
			makeBarLineOption({
				data: revenueByDateData,
				xKey: "date",
				barKey: "revenue",
				lineKey: "rpm",
				barName: "Revenue",
				lineName: "RPM",
				leftAxisLabel: "Revenue",
				rightAxisLabel: "RPM",
				leftFormatter: (v) => `$${Number(v || 0).toFixed(0)}`,
				rightFormatter: rpmFmt,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.2,
				splitNumber: 5,
				minBarPx: 3
			}),
		[revenueByDateData]
	);

	const optSolutionRevenue = useMemo(
		() =>
			makeBarLineOption({
				data: solutionRevenueChartData,
				xKey: "solution",
				barKey: "revenue",
				lineKey: "rpm",
				barName: "Revenue",
				lineName: "RPM",
				leftAxisLabel: "Revenue",
				rightAxisLabel: "RPM",
				leftFormatter: (v) => `$${Number(v || 0).toFixed(0)}`,
				rightFormatter: rpmFmt,
				barGradientTop: "#60a5fa",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.2,
				splitNumber: 5,
				minBarPx: 3
			}),
		[solutionRevenueChartData]
	);

	const optTopDomainsRevenue = useMemo(
		() =>
			makeBarLineOption({
				data: topDomainsRevenueData,
				xKey: "domain",
				barKey: "revenue",
				lineKey: "rpm",
				barName: "Revenue",
				lineName: "RPM",
				leftAxisLabel: "Revenue",
				rightAxisLabel: "RPM",
				leftFormatter: (v) => `$${Number(v || 0).toFixed(0)}`,
				rightFormatter: rpmFmt,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.18,
				splitNumber: 5,
				minBarPx: 3
			}),
		[topDomainsRevenueData]
	);

	const optTopCountriesRevenue = useMemo(
		() =>
			makeBarLineOption({
				data: topCountriesRevenueData,
				xKey: "country",
				barKey: "revenue",
				lineKey: "rpm",
				barName: "Revenue",
				lineName: "RPM",
				leftAxisLabel: "Revenue",
				rightAxisLabel: "RPM",
				leftFormatter: (v) => `$${Number(v || 0).toFixed(0)}`,
				rightFormatter: rpmFmt,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.2,
				splitNumber: 5,
				minBarPx: 3
			}),
		[topCountriesRevenueData]
	);

	const optEngByDate = useMemo(
		() =>
			makeBarLineOption({
				data: analyticsByDateData,
				xKey: "date",
				barKey: "engagementHours",
				lineKey: "boostPercent",
				barName: "Engagement Time",
				lineName: "Engagement Boost",
				leftAxisLabel: "Hours",
				rightAxisLabel: "Boost (%)",
				leftFormatter: (v) => `${Number(v || 0).toFixed(0)}h`,
				rightFormatter: (v) => `${Number(v || 0).toFixed(0)}%`,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.2,
				splitNumber: 5,
				minBarPx: 3
			}),
		[analyticsByDateData]
	);

	const optSolutionEng = useMemo(
		() =>
			makeBarLineOption({
				data: solutionAnalyticsData,
				xKey: "solution",
				barKey: "engagementHours",
				lineKey: "boostPercent",
				barName: "Engagement Time",
				lineName: "Engagement Boost",
				leftAxisLabel: "Hours",
				rightAxisLabel: "Boost (%)",
				leftFormatter: (v) => `${Number(v || 0).toFixed(0)}h`,
				rightFormatter: (v) => `${Number(v || 0).toFixed(0)}%`,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.2,
				splitNumber: 5,
				minBarPx: 3
			}),
		[solutionAnalyticsData]
	);

	const optDomainEng = useMemo(
		() =>
			makeBarLineOption({
				data: topDomainsAnalyticsData,
				xKey: "domain",
				barKey: "engagementHours",
				lineKey: "boostPercent",
				barName: "Engagement Time",
				lineName: "Engagement Boost",
				leftAxisLabel: "Hours",
				rightAxisLabel: "Boost (%)",
				leftFormatter: (v) => `${Number(v || 0).toFixed(0)}h`,
				rightFormatter: (v) => `${Number(v || 0).toFixed(0)}%`,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.18,
				splitNumber: 5,
				minBarPx: 3
			}),
		[topDomainsAnalyticsData]
	);

	const optCountryEng = useMemo(
		() =>
			makeBarLineOption({
				data: topCountriesAnalyticsData,
				xKey: "country",
				barKey: "engagementHours",
				lineKey: "boostPercent",
				barName: "Engagement Time",
				lineName: "Engagement Boost",
				leftAxisLabel: "Hours",
				rightAxisLabel: "Boost (%)",
				leftFormatter: (v) => `${Number(v || 0).toFixed(0)}h`,
				rightFormatter: (v) => `${Number(v || 0).toFixed(0)}%`,
				barGradientTop: "#93c5fd",
				barGradientBottom: "#2563eb",
				lineColor: "#f97316",
				is3D: true,
				barWidthRatio: 0.2,
				splitNumber: 5,
				minBarPx: 3
			}),
		[topCountriesAnalyticsData]
	);

	return (
		<div className="space-y-6">
			<DashboardHeader
				onDateChange={handleDateChangeFromHeader}
				domainOptions={domainOptions}
				selectedDomain={selectedDomain}
				onDomainChange={setSelectedDomain}
				onDownload={handleDownload}
				disableDownload={!hasLoadedOnce || loadingAll}
			/>

			{!partnerName && (
				<div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
					No partner selected / resolved.
				</div>
			)}

			{errorMsg && (
				<div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
					{errorMsg}
				</div>
			)}

			{/* ✅ BLOCK UI UNTIL FIRST SUCCESSFUL LOAD */}
			{isInitialBlockingLoading ? (
				<div className="space-y-6">
					<section className="space-y-4">
						<h2 className="text-base md:text-lg font-semibold text-slate-900">
							Monetization Overview
						</h2>
						<div className="grid gap-4 md:grid-cols-3">
							<SkeletonStat />
							<SkeletonStat />
							<SkeletonStat />
						</div>
						<div className="grid gap-4 lg:grid-cols-2">
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
						</div>
					</section>

					<section className="space-y-4">
						<h2 className="text-base md:text-lg font-semibold text-slate-900">
							Analytics Overview
						</h2>
						<div className="grid gap-4 md:grid-cols-3">
							<SkeletonStat />
							<SkeletonStat />
							<SkeletonStat />
						</div>
						<div className="grid gap-4 lg:grid-cols-2">
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
							<SkeletonCard />
						</div>
					</section>

					{loadingAll && (
						<div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
							Loading dashboard data...
						</div>
					)}
				</div>
			) : (
				<>
					{loadingAll && (
						<div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
							Loading dashboard data...
						</div>
					)}

					<section className="space-y-4">
						<h2 className="text-base md:text-lg font-semibold text-slate-900">
							Monetization Overview
						</h2>

						<div className="grid gap-4 md:grid-cols-3">
							<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center">
								<div className="text-xs font-semibold text-emerald-500 tracking-wide">
									Inventory
								</div>
								<div className="mt-1 text-2xl font-bold text-slate-900">
									{revenueSummaryData.inventoryCount}
								</div>
							</div>

							<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center">
								<div className="text-xs font-semibold text-sky-500 tracking-wide">
									Revenue
								</div>
								<div className="mt-1 text-2xl font-bold text-slate-900">
									${revenueSummaryData.totalRevenue}
								</div>
							</div>

							<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center">
								<div className="text-xs font-semibold text-orange-500 tracking-wide">
									RPM
								</div>
								<div className="mt-1 text-2xl font-bold text-slate-900">
									${revenueSummaryData.rpm}
								</div>
							</div>
						</div>

						<div className="grid gap-4 lg:grid-cols-2">
							<ChartCard title="Revenue & RPM by Date">
								<Chart option={optRevenueByDate} />
							</ChartCard>

							<ChartCard title="Solution-Specific Revenue & RPM">
								<Chart option={optSolutionRevenue} />
							</ChartCard>

							<ChartCard title="Top Domains by Revenue & RPM">
								<Chart option={optTopDomainsRevenue} />
							</ChartCard>

							<div className="relative">
								<ChartCard title="Top Countries by Revenue & RPM">
									<Chart option={optTopCountriesRevenue} />
								</ChartCard>
								{loadingCountries && (
									<InlineOverlayLoading text="Refreshing country revenue..." />
								)}
							</div>
						</div>
					</section>

					<section className="space-y-4">
						<h2 className="text-base md:text-lg font-semibold text-slate-900">
							Analytics Overview
						</h2>

						<div className="grid gap-4 md:grid-cols-3">
							<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center">
								<div className="text-xs font-semibold text-sky-500 tracking-wide">
									Engagement Time
								</div>
								<div className="mt-1 text-xl md:text-2xl font-bold text-slate-900">
									{analyticsSummaryData.engagementTimeHHMMSS}
								</div>
							</div>

							<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center">
								<div className="text-xs font-semibold text-emerald-500 tracking-wide">
									CTR
								</div>
								<div className="mt-1 text-xl md:text-2xl font-bold text-slate-900">
									{Number(analyticsSummaryData.ctr || 0).toFixed(2)}%
								</div>
							</div>

							<div className="rounded-2xl bg-white shadow-sm border border-sky-100 px-6 py-4 flex flex-col items-center text-center">
								<div className="text-xs font-semibold text-orange-500 tracking-wide">
									Engagement Boost
								</div>
								<div className="mt-1 text-xl md:text-2xl font-bold text-slate-900">
									{Number(analyticsSummaryData.engagementBoost || 0).toFixed(2)}
									%
								</div>
							</div>
						</div>

						<div className="grid gap-4 lg:grid-cols-2">
							<ChartCard title="Engagement Time & Boost by Date">
								<Chart option={optEngByDate} />
							</ChartCard>

							<ChartCard title="Solution-Specific Engagement">
								<Chart option={optSolutionEng} />
							</ChartCard>

							<ChartCard title="Domain-wise Engagement Time & Engagement Boost">
								<Chart option={optDomainEng} />
							</ChartCard>

							<div className="relative">
								<ChartCard title="Country-wise Engagement Time & Engagement Boost">
									<Chart option={optCountryEng} />
								</ChartCard>
								{loadingCountries && (
									<InlineOverlayLoading text="Refreshing country analytics..." />
								)}
							</div>
						</div>
					</section>
				</>
			)}
		</div>
	);
};

export default Dashboard;

/* ------------------------------------------------------------------ */
/* HEADER COMPONENT: date range + domain dropdown                      */
/* ------------------------------------------------------------------ */

const DashboardHeader = ({
	onDateChange,
	domainOptions,
	selectedDomain,
	onDomainChange,
	onDownload,
	disableDownload = false
}) => {
	const [dateRange, setDateRange] = useState({ start: null, end: null });
	const [selectedOption, setSelectedOption] = useState("last7days");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [customStart, setCustomStart] = useState(null);
	const [customEnd, setCustomEnd] = useState(null);

	const today = new Date();
	const maxDate = subDays(today, 2);
	const minDate = subDays(maxDate, 44);

	useEffect(() => {
		const endDate = maxDate;
		const startDate = subDays(endDate, 6);
		const initial = { start: startDate, end: endDate };
		setDateRange(initial);
		onDateChange?.(initial);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (dateRange.start && dateRange.end) onDateChange?.(dateRange);
	}, [dateRange, onDateChange]);

	const applyPresetRange = (option) => {
		let start, end;
		end = maxDate;

		switch (option) {
			case "last7days":
				start = subDays(end, 6);
				break;
			case "last30days":
				start = subDays(end, 29);
				break;
			case "thisMonth":
				start = startOfMonth(end);
				break;
			default:
				return;
		}

		setSelectedOption(option);
		setDateRange({ start, end });
		setIsDropdownOpen(false);
	};

	const handleOptionSelect = (option) => {
		if (option === "custom") {
			setSelectedOption("custom");
			setCustomStart(dateRange.start || minDate);
			setCustomEnd(dateRange.end || maxDate);
			return;
		}
		applyPresetRange(option);
	};

	const handleCustomDateChange = (dates) => {
		const [start, end] = dates;
		setCustomStart(start);
		setCustomEnd(end);
		if (!start || !end) return;
		setDateRange({ start, end });
		setSelectedOption("custom");
		setIsDropdownOpen(false);
	};

	const formatDateRange = () => {
		if (!dateRange.start || !dateRange.end) return "Select Date Range";
		return `${format(dateRange.start, "yyyy-MM-dd")} - ${format(
			dateRange.end,
			"yyyy-MM-dd"
		)}`;
	};

	return (
		<div className="flex flex-col gap-3 rounded-lg border border-sky-100 bg-sky-50 px-3 sm:px-4 py-3 text-xs">
			<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
				<div className="flex flex-col gap-1">
					<div className="flex flex-wrap items-center gap-2">
						<div className="relative">
							<button
								onClick={() => setIsDropdownOpen((prev) => !prev)}
								className="inline-flex max-w-[260px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50"
							>
								<span>📅</span>
								<span className="truncate">{formatDateRange()}</span>
							</button>

							{isDropdownOpen && (
								<div className="absolute left-0 top-full mt-2 z-50 w-[min(100vw-2rem,640px)] rounded-lg border border-slate-200 bg-white shadow-lg">
									<div className="flex flex-col sm:flex-row">
										<div className="w-full sm:w-40 border-b sm:border-b-0 sm:border-r border-slate-200">
											<div className="flex flex-col py-2 text-xs">
												<button
													onClick={() => handleOptionSelect("last7days")}
													className={`px-3 py-2 text-left hover:bg-slate-50 ${
														selectedOption === "last7days"
															? "bg-sky-50 text-sky-700 font-semibold"
															: ""
													}`}
												>
													Last 7 Days
												</button>
												<button
													onClick={() => handleOptionSelect("last30days")}
													className={`px-3 py-2 text-left hover:bg-slate-50 ${
														selectedOption === "last30days"
															? "bg-sky-50 text-sky-700 font-semibold"
															: ""
													}`}
												>
													Last 30 Days
												</button>
												<button
													onClick={() => handleOptionSelect("thisMonth")}
													className={`px-3 py-2 text-left hover:bg-slate-50 ${
														selectedOption === "thisMonth"
															? "bg-sky-50 text-sky-700 font-semibold"
															: ""
													}`}
												>
													This Month
												</button>
												<button
													onClick={() => handleOptionSelect("custom")}
													className={`px-3 py-2 text-left hover:bg-slate-50 ${
														selectedOption === "custom"
															? "bg-sky-50 text-sky-700 font-semibold"
															: ""
													}`}
												>
													Custom Range
												</button>
											</div>
										</div>

										<div className="w-full sm:flex-1 p-2">
											{selectedOption === "custom" ? (
												<div className="rounded-md bg-slate-50 p-2">
													<p className="mb-1 text-[11px] font-semibold text-slate-700">
														Select custom range (within last 45 days)
													</p>
													<DatePicker
														selectsRange
														startDate={customStart}
														endDate={customEnd}
														onChange={handleCustomDateChange}
														minDate={minDate}
														maxDate={maxDate}
														monthsShown={2}
														showPreviousMonths
														inline
														openToDate={maxDate}
														calendarClassName="custom-range-calendar"
													/>
												</div>
											) : (
												<div className="flex h-full items-center justify-center text-[11px] text-slate-500">
													Choose{" "}
													<span className="font-semibold">
														&nbsp;Custom Range&nbsp;
													</span>{" "}
													on the left.
												</div>
											)}
										</div>
									</div>

									<div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-[11px]">
										<span className="text-slate-500">
											{dateRange.start && dateRange.end
												? formatDateRange()
												: "No range selected"}
										</span>
										<button
											className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-50"
											onClick={() => setIsDropdownOpen(false)}
										>
											Close
										</button>
									</div>
								</div>
							)}
						</div>

						{/* ✅ Download */}
						<button
							className={`flex h-8 w-8 items-center justify-center rounded-md text-lg ${
								disableDownload
									? "text-slate-300 cursor-not-allowed"
									: "text-slate-500 hover:bg-white"
							}`}
							title={
								disableDownload
									? "Download disabled (data not loaded yet)"
									: "Download CSV"
							}
							onClick={onDownload}
							disabled={disableDownload}
						>
							⬇
						</button>

						{/* Refresh */}
						<button
							className="flex h-8 w-8 items-center justify-center rounded-md text-lg text-slate-500 hover:bg-white"
							title="Refresh"
							onClick={() => applyPresetRange(selectedOption)}
						>
							↻
						</button>
					</div>

					<p className="w-full text-[11px] text-slate-600 text-left md:text-right">
						Disclaimer: Typically, it takes 48 hours to finalize all data
					</p>
				</div>

				<div className="flex items-center justify-between md:justify-end gap-4">
					<div className="flex flex-col items-end">
						<span className="text-[10px] text-slate-500 mb-0.5">
							Select Domain
						</span>
						<div className="rounded-md bg-slate-100 px-2 py-1 flex items-center gap-1">
							<select
								value={selectedDomain}
								onChange={(e) => onDomainChange(e.target.value)}
								className="bg-transparent text-xs font-medium text-slate-800 focus:outline-none"
							>
								{domainOptions.map((d) => (
									<option
										key={d}
										value={d}
									>
										{d}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const ChartCard = ({ title, children }) => (
	<div className="rounded-2xl bg-white shadow-sm border border-sky-100 p-4">
		<h3 className="mb-2 text-sm font-semibold text-slate-800">{title}</h3>
		{children}
	</div>
);
